/**
 * WorkspaceSwitcher — the tenant picker that lives in the top bar, in place of
 * the old static workspace pill.
 *
 * The active tenant is the session's `activeOrganizationId`; the list of tenants
 * to switch between comes from the Better Auth organization plugin. Selecting a
 * different org calls `setActive` (which re-scopes every server request to that
 * tenant) and then MUST clear the react-query cache — cached data is
 * workspace-scoped but NOT namespaced by org, so without the clear the UI would
 * briefly show the previous tenant's chats/keys (a cross-tenant leak).
 */
import { useNavigate } from '@tanstack/react-router';
import { Building2, Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { authClient } from '@/lib/auth-client';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { CreateOrgDialog } from './create-org-dialog';

export function WorkspaceSwitcher() {
  const navigate = useNavigate();
  const { data: organizations, isPending } = authClient.useListOrganizations();
  const { data: session } = authClient.useSession();

  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeId = session?.session.activeOrganizationId ?? null;
  const orgs = organizations ?? [];
  const active = orgs.find((o) => o.id === activeId) ?? null;

  // Close the panel on an outside click.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Close the panel on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  async function handleSelect(organizationId: string) {
    if (organizationId === activeId || switchingId) return;
    setSwitchingId(organizationId);
    try {
      const { error } = await authClient.organization.setActive({ organizationId });
      if (error) {
        toast.error(error.message ?? 'Could not switch workspace');
        return;
      }
      // Cache is tenant-scoped but not keyed by tenant — drop it all so the next
      // fetch belongs to the org we just switched to.
      queryClient.clear();
      setOpen(false);
      void navigate({ to: '/dashboard' });
    } catch {
      toast.error('Could not switch workspace');
    } finally {
      setSwitchingId(null);
    }
  }

  return (
    <div ref={containerRef} className="relative ml-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-full border border-border-subtle px-2.5 py-[3px] text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        {isPending ? (
          <Skeleton className="h-3.5 w-16" />
        ) : (
          <span className="max-w-[160px] truncate">{active?.name ?? 'Workspace'}</span>
        )}
        <ChevronsUpDown className="h-3 w-3 shrink-0" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-[calc(100%+6px)] z-50 w-64 overflow-hidden rounded-lg border border-border bg-background p-1 shadow-lg"
        >
          <div className="max-h-72 overflow-y-auto">
            {orgs.map((org) => {
              const isActive = org.id === activeId;
              const isSwitching = switchingId === org.id;
              return (
                <button
                  key={org.id}
                  type="button"
                  role="menuitem"
                  disabled={switchingId !== null}
                  onClick={() => void handleSelect(org.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent disabled:cursor-default disabled:opacity-60',
                    isActive ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-faint" />
                  <span className="min-w-0 flex-1 truncate">{org.name}</span>
                  {isSwitching ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-faint" />
                  ) : isActive ? (
                    <Check className="h-4 w-4 shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="my-1 h-px bg-border-subtle" />

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setCreateOpen(true);
            }}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-4 w-4 shrink-0" />
            Create workspace
          </button>
        </div>
      ) : null}

      <CreateOrgDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
